import { useEffect, useRef, useState } from "react";

// LN003, manual 0.3.0 §8: 27m / 3.20% / 30 days → 24m / 2.60% / 30 days.
// These are fixed explanatory examples, never live task data or a new financial rule.
const chapters = [
  {
    label: "识别差额",
    title: "利息下降了，究竟是哪一项发生变化？",
    body: "同一合同、同一自然月口径，本期利息由同期的 72,000 元降至 52,000 元。20,000 元的下降，是分析的起点。",
    takeaway: "先对齐对象、期间与口径，再判断变化。",
  },
  {
    label: "拆解原因",
    title: "把差额拆开，才看得见管理着力点。",
    body: "按天数、规模、有效利率依次替换：本金规模变化贡献 −8,000 元，有效利率变化贡献 −12,000 元。本例计息天数相同，天数影响为 0。",
    takeaway: "区分规模与利率影响，为融资安排提供量化依据。",
  },
  {
    label: "核查依据",
    title: "每一个解释，都能回到它的依据。",
    body: "从因素贡献回看合同本金、有效利率及计息期间。结果、条件与来源一起保留，让分析人员、运营人员和负责人讨论同一套事实。",
    takeaway: "解释可以复核，判断才有共同的基础。",
  },
] as const;

export default function CostStory() {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [reduced, setReduced] = useState(
    () =>
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
  );
  const section = useRef<HTMLElement>(null);
  useEffect(() => {
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const update = () => {
      setReduced(media?.matches ?? false);
      if (media?.matches) setPlaying(false);
    };
    media?.addEventListener("change", update);
    const hide = () => {
      if (document.hidden) setPlaying(false);
    };
    document.addEventListener("visibilitychange", hide);
    const observer =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) setPlaying(false);
          });
    if (section.current) observer?.observe(section.current);
    return () => {
      media?.removeEventListener("change", update);
      document.removeEventListener("visibilitychange", hide);
      observer?.disconnect();
    };
  }, []);
  useEffect(() => {
    if (!playing || reduced) return;
    const timeout = window.setTimeout(() => {
      if (step < chapters.length - 1) setStep(step + 1);
      else setPlaying(false);
    }, 1600);
    return () => window.clearTimeout(timeout);
  }, [playing, reduced, step]);
  function select(index: number) {
    setPlaying(false);
    setStep(index);
  }
  function play() {
    if (reduced) {
      select(2);
      return;
    }
    if (playing) setPlaying(false);
    else {
      setStep(0);
      setPlaying(true);
    }
  }
  return (
    <section
      ref={section}
      id="insight"
      className="product-section product-cost-story"
      aria-labelledby="cost-story-title"
    >
      <div className="product-story-heading">
        <div>
          <h2 id="cost-story-title">
            成本变化，
            <br />
            不止于一个差额。
          </h2>
          <p>从“少了多少”追问“为什么减少”，把数字转化为可讨论的管理信息。</p>
        </div>
        <button type="button" className="product-story-play" onClick={play}>
          <svg
            viewBox="0 0 20 20"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            {playing ? (
              <path d="M7 4v12M13 4v12" />
            ) : (
              <path d="m6 3 10 7-10 7Z" />
            )}
          </svg>
          {reduced ? "查看完整分析" : playing ? "暂停播放" : "播放分析过程"}
        </button>
      </div>
      <div className="product-story-controls" aria-label="成本归因步骤">
        {chapters.map((chapter, index) => (
          <button
            type="button"
            key={chapter.label}
            onClick={() => select(index)}
            aria-pressed={step === index}
            aria-controls="cost-story-content"
          >
            <span>0{index + 1}</span>
            {chapter.label}
          </button>
        ))}
      </div>
      <div
        id="cost-story-content"
        className="product-story-layout"
        data-step={step}
      >
        <figure className="product-waterfall">
          <figcaption>
            <strong>飞机购置融资 · 利息同比归因</strong>
            <span>示例合同 LN003 · 2026年9月 · 单位：元</span>
          </figcaption>
          <svg
            className="product-waterfall-chart"
            viewBox="0 0 640 370"
            role="img"
            aria-labelledby="waterfall-title waterfall-description"
          >
            <title id="waterfall-title">利息从 72,000 元降至 52,000 元</title>
            <desc id="waterfall-description">
              规模影响减少 8,000 元，有效利率影响减少 12,000
              元，计息天数影响为零，合计减少 20,000 元。
            </desc>
            <g className="product-chart-grid">
              <path d="M54 72H610M54 172H610M54 272H610" />
              <text x="45" y="77" textAnchor="end">
                72,000
              </text>
              <text x="45" y="177" textAnchor="end">
                36,000
              </text>
              <text x="45" y="277" textAnchor="end">
                0
              </text>
            </g>
            <rect
              className="product-chart-before"
              x="86"
              y="72"
              width="82"
              height="200"
              rx="3"
            />
            <text
              className="product-chart-value"
              x="127"
              y="56"
              textAnchor="middle"
            >
              72,000
            </text>
            <text
              className="product-chart-label"
              x="127"
              y="305"
              textAnchor="middle"
            >
              同期利息
            </text>
            <g
              className="product-chart-factors"
              data-visible={step > 0}
              aria-hidden={step === 0}
            >
              <path
                className="product-chart-connector"
                d="M168 72H228M310 94.222H370M452 127.556H512"
              />
              <g className="product-chart-factor">
                <rect x="228" y="72" width="82" height="22.222" rx="2" />
                <text
                  className="product-chart-value"
                  x="269"
                  y="56"
                  textAnchor="middle"
                >
                  −8,000
                </text>
                <text
                  className="product-chart-label"
                  x="269"
                  y="305"
                  textAnchor="middle"
                >
                  规模影响
                </text>
              </g>
              <g className="product-chart-factor product-chart-factor-rate">
                <rect x="370" y="94.222" width="82" height="33.334" rx="2" />
                <text
                  className="product-chart-value"
                  x="411"
                  y="79"
                  textAnchor="middle"
                >
                  −12,000
                </text>
                <text
                  className="product-chart-label"
                  x="411"
                  y="305"
                  textAnchor="middle"
                >
                  利率影响
                </text>
              </g>
            </g>
            <rect
              className="product-chart-after"
              x="512"
              y="127.556"
              width="82"
              height="144.444"
              rx="3"
            />
            <text
              className="product-chart-value"
              x="553"
              y="112"
              textAnchor="middle"
            >
              52,000
            </text>
            <text
              className="product-chart-label"
              x="553"
              y="305"
              textAnchor="middle"
            >
              本期利息
            </text>
            <g className="product-chart-total" data-visible={step === 0}>
              <path d="M184 177H492" />
              <text x="338" y="164" textAnchor="middle">
                同比减少 20,000 元
              </text>
            </g>
            <text className="product-chart-footnote" x="54" y="352">
              同口径比较 · 自然月利息 · ACT/360
            </text>
          </svg>
          <div className="product-story-equation">
            <span>72,000</span>
            <span>− 8,000</span>
            <span>− 12,000</span>
            <strong>= 52,000</strong>
          </div>
        </figure>
        <div className="product-story-explanation" aria-live="polite">
          <div key={step} className="product-story-chapter">
            <h3>{chapters[step].title}</h3>
            <p>{chapters[step].body}</p>
            {step === 2 ? (
              <table className="product-story-evidence">
                <caption>本例的比较依据</caption>
                <thead>
                  <tr>
                    <th scope="col">条件</th>
                    <th scope="col">同期</th>
                    <th scope="col">本期</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">本金／万元</th>
                    <td>2,700</td>
                    <td>2,400</td>
                  </tr>
                  <tr>
                    <th scope="row">有效年利率</th>
                    <td>3.20%</td>
                    <td>2.60%</td>
                  </tr>
                  <tr>
                    <th scope="row">计息天数</th>
                    <td>30</td>
                    <td>30</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <p className="product-story-takeaway">
                {chapters[step].takeaway}
              </p>
            )}
          </div>
        </div>
      </div>
      <p className="product-story-source">
        案例依据：手工计算说明书
        0.3.0。本例按天数、规模、有效利率顺序替换，用于说明分析方法。
      </p>
    </section>
  );
}
